/* eslint-disable no-nested-ternary */
import DistanceBy from "./components/DistanceBy";
import getLocation, { getDistanceData, isAvailable, getPropertyFilter, getLink } from "./helper";
import styles from "./searchListItemV2.module.scss";
import BottomSection from "./components/BottomSection";
import { AppState } from "@Store/index";
import FeatureTags from "./components/FeatureTags";
import ImageCarouselWithTags from "./components/ImageCarouselWithTags";
import InventoryDetailTags from "./components/InventoryDetailTags";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useSelector, shallowEqual } from "react-redux";
import { PAGE, CANNIBALIZATION_ROLLOUT } from "constants/configConstants";
import CustomLink from "@Components/CustomLink";
import { memo, useRef, useState } from "react";
import { ISearchListItem } from "./types";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES, SEARCH_PAGE_CTA_VALUES } from "constants/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import getSortedTags from "./utils/getSortedTags";
import getSortedTagPairs from "./utils/getSortedTagPairs";
import {
  getCommonAmenities,
  getTags,
  milesToKilometersConverter,
} from "@Pages/PropertyPage/components/PropertyDetailsSection/helper";
import { useFeatureValue } from "@growthbook/growthbook-react";
import wrapperHOC from "@Utils/wrapperHOC";
import config from "@Config/index";
import useWhitelabel from "hooks/useWhitelabel";
import GraduationCapThin from "@Icons/GraduationCapThin";
import useTranslationHook from "hooks/useTranslationHook";
import ShortListButton from "./components/ShortListButton";
import setCookie from "helpers/setCookie";
import Image from "@Components/Image";
import { useAmberSales } from "hooks/useAmberSales";
import loadable from "@loadable/component";
import FullPageLoader from "@Components/FullPageLoader";
import { convertToFilterObj } from "@Pages/Search/utils/formatFilterState";
import useIsIframe from "hooks/useIsIframe";
import { getIframeSourceParam } from "@Utils/iframeContext";
import PropertyTags from "./components/PropertyTags";
import ReviewSummaryRating from "./components/ReviewSummaryRating";
import NearbyDistanceInfo from "../NearbyDistanceInfo/NearbyDistanceInfo";

const AgentViewCard = loadable(() => import("./components/AgentViewCard"), {
  fallback: <FullPageLoader />,
});

const SearchListItemDesktop = (props: ISearchListItem) => {
  const {
    images,
    videos,
    name,
    location,
    meta_short,
    destination_distances,
    nearby_distances,
    regionType,
    id,
    pricing,
    feature_tags,
    index,
    canonical_name,
    regionId,
    distance_from_searched_item,
    reviews_rating,
    reviews_count,
    review_summary,
    features,
    tags,
    tags_en,
    region,
    isShortlistPageCard = false,
    shortlistedCardID = 0,
    page,
    showCTA = true,
    meta_real,
    loadedCount,
    isAgentView,
    customLocation,
    whiteLabelPartnerData,
    isGridView,
    nearby_distance_miles,
  } = props;
  const isCardPillsV2 = true;
  const [highlighted] = useState(false);
  const isInventoryAvailable = isAvailable(props);

  const searchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  // Check if locality includes "near-by" to match the region name pattern
  const localityParam = searchParams.get("locality");
  const nearbyActive = localityParam ? localityParam.includes("near-by") : false;
  const showNearbyDistance = nearbyActive && nearby_distance_miles != null;

  const unsortedTags = getTags(feature_tags) || [];
  const tagsArray = getSortedTags(unsortedTags) || [];

  const { display: sortedTags, en: sortedTagsEn } = getSortedTagPairs(tags, tags_en);
  const updatedTags = tagsArray?.concat(getCommonAmenities(sortedTags, features));
  const rawUpdatedTagsEn = tagsArray?.concat(getCommonAmenities(sortedTagsEn, features, true));
  const updatedTagsEn = updatedTags?.map((tag: string, i: number) => rawUpdatedTagsEn?.[i] ?? tag);

  // CRO tags
  const showRatings = Boolean(isInventoryAvailable && reviews_rating >= "4.0"); // P1D-3500
  const locationName = getLocation(location, [
    "route_short_name",
    "locality_long_name",
    "postal_code",
  ]);
  const distanceData = distance_from_searched_item || getDistanceData(destination_distances);

  const nearestUni = (nearby_distances as any[])
    ?.filter((d: any) => d.place?.toLowerCase() !== "city center")
    .reduce((nearest: any, current: any) => {
      if (!nearest) return current;
      const currentDist = parseFloat(current.distance);
      const nearestDist = parseFloat(nearest.distance);
      if (currentDist < nearestDist) return current;
      if (currentDist === nearestDist && current.place?.localeCompare(nearest.place) < 0)
        return current;
      return nearest;
    }, null) as Record<string, any> | null;

  const { t } = useTranslationHook();
  const fromText = t("searchpage.searchlist.searchlistitem.from");

  const { userData, isAgentViewOpened, searchFilters, userLanguage } = useSelector(
    ({ appData, searchPageData }: AppState) => ({
      userData: appData.userData,
      isAgentViewOpened: searchPageData.isAgentViewOpened,
      searchFilters: searchPageData.filters,
      userLanguage: appData.userLanguage,
    }),
    shallowEqual,
  );

  const { isDemoSubdomain, partner_uuid, isWhitelabel } = useWhitelabel();
  const { isIframe, iframeSource } = useIsIframe();
  const params = getPropertyFilter(convertToFilterObj(searchFilters));
  const updatedPrams = { ...params };
  // if (regionType === "establishment") {
  //   Object.assign(updatedPrams, { regionId });
  // }
  if (isDemoSubdomain) {
    Object.assign(updatedPrams, { partner_id: partner_uuid });
  }
  // Add whitelabelv2 params if a white label partner is selected
  if (whiteLabelPartnerData && whiteLabelPartnerData?.whitelabel_domain_url) {
    Object.assign(updatedPrams, {
      whitelabelv2: true,
      utm_source: whiteLabelPartnerData.uuid,
    });
  }
  Object.assign(updatedPrams, getIframeSourceParam(isIframe, iframeSource));
  const link = getLink(props, updatedPrams);

  const imageWrapperRef = useRef(null);
  const bottomRef = useRef(null);

  const handlePropertyCardClick = () => {
    if (isShortlistPageCard) {
      gtagEventEmitter({
        action: "shortlist_page_property_view",
        category: PAGE.PROFILE,
        label: `${name}${location}`,
        canonical_name,
        email_id: userData?.email,
      });
    }
    gtagEventEmitter({
      action: "inventory_card_clicked",
      category: PAGE.SEARCH,
      label: `${name}${location}`,
      canonical_name,
      email_id: userData?.email,
    });
    if (regionId && !config.IS_CHINA_APP) {
      setCookie({
        cname: "searchRegionId",
        cvalue: regionId,
        exdays: region?.location?.name !== location?.locality?.long_name ? 365 : -1,
      });
    }
  };

  const highlightInventory = () => {
    const event = new Event(`HOVER_INVENTORY_${id}_ADD`);
    window.dispatchEvent(event);
  };

  const highlightRemoveInventory = () => {
    const event = new Event(`HOVER_INVENTORY_${id}_REMOVE`);
    window.dispatchEvent(event);
  };

  const ref: any = useRef();

  const onKeyDown = (e: any) => {
    if (e.key === "Enter") {
      handlePropertyCardClick();
    }
  };

  // AU-only: Check for university filter
  const isAU = CANNIBALIZATION_ROLLOUT.includes(region?.location?.country?.short_name || "");
  const shouldShowNearestUni = isAU
    ? regionType !== "establishment" && !searchFilters?.university
    : regionType !== "establishment";

  const enquireFlow = useFeatureValue(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.WITHOUT_ENQUIRE,
    SEARCH_PAGE_CTA_VALUES.OFF,
  );

  const isEnquireDisabled = enquireFlow === SEARCH_PAGE_CTA_VALUES.NO_CTA && page === PAGE.SEARCH;
  const showViewProperty =
    enquireFlow === SEARCH_PAGE_CTA_VALUES.VIEW_PROPERTY_CTA && page === PAGE.SEARCH;
  const showEnquireButton = showCTA && !isEnquireDisabled;

  const customMetaForSaleOnlyChina = {
    cro_tags: {
      amber_sale: {
        offer:
          meta_real?.cro_tags?.amber_sale?.offer || meta_short?.cro_tags?.amber_sale?.offer || "",
        status: meta_short?.cro_tags?.amber_sale?.status || "",
      },
    },
  };
  const {
    isSaleOfferAvailableOnInventory,
    salesOfferTextForInventory,
    inventoryBrandImage,
    primaryColor,
    borderColor,
    backgroundGradient,
  }: any = useAmberSales({
    inventoryMeta: config.IS_CHINA_APP ? customMetaForSaleOnlyChina : meta_short,
    page: "SEARCH",
  });

  const containerStyle =
    loadedCount && loadedCount <= 10
      ? { viewTransitionName: `search-item-${id || index}` }
      : undefined;

  const SaleOfferBrandTag = () => (
    <div className={styles.saleOfferBannerContainer} style={{ background: primaryColor }}>
      <div className={styles.imageContainer} style={{ background: backgroundGradient }}>
        <Image
          src={inventoryBrandImage}
          alt="sale-offer-banner"
          width="90%"
          height="auto"
          isEagerLoad
        />
      </div>
      <div className={formatClassNames(styles.saleOfferText, styles.saleOfferTextListView)}>
        {salesOfferTextForInventory}
      </div>
    </div>
  );

  const renderContent = () => (
    <div className={styles.contentSection}>
      <div className={formatClassNames(styles.leftSection, isAgentView && styles.isAgentView)}>
        <div className={styles.title}>
          <div className={styles.titleText}>
            <h3
              className={formatClassNames(isWhitelabel ? "wl-title-font" : "", styles.heading)}
              data-testid="searchpage-inventoryCard-propertyName"
            >
              {name}
            </h3>
            <div
              className={styles.subHeading}
              data-testid="searchpage-inventoryCard-propertyLocation"
            >
              <span>{locationName}</span>
            </div>
          </div>
        </div>
        {showNearbyDistance ? (
          <NearbyDistanceInfo
            nearbyDistanceMiles={nearby_distance_miles as number}
            regionType={regionType}
            regionName={region?.name}
          />
        ) : (
          <DistanceBy
            data={distanceData}
            regionType={regionType}
            region={region}
            addTuncateForDistance
            customLocation={customLocation}
            isAgentView={isAgentView}
          />
        )}
        {nearestUni && nearestUni.distance && nearestUni.place && shouldShowNearestUni && (
          <div className={styles.nearestUniRow} data-testid="searchPage-inventoryCard-nearestUni">
            <GraduationCapThin />
            <span className={styles.distanceText}>
              {`${
                config.IS_CHINA_APP
                  ? milesToKilometersConverter(nearestUni.distance)
                  : nearestUni.distance
              } ${fromText} ${nearestUni[`place_${userLanguage}`] || nearestUni.place}`}
            </span>
          </div>
        )}
        <div className={styles.dottedDivider} />
        {isCardPillsV2 ? (
          <InventoryDetailTags
            item={props}
            tags={updatedTags}
            tags_en={updatedTagsEn}
            dataTestId="searchPage-inventoryCard-tags"
            showRatings={showRatings}
            isScrollable={isGridView}
          />
        ) : (
          <>
            <FeatureTags data={getSortedTags(tags)} />
            <InventoryDetailTags
              item={props}
              dataTestId="searchPage-inventoryCard-tags"
              showRatings={showRatings}
              isScrollable={isGridView}
            />
          </>
        )}
      </div>
      <div className={styles.rightSection}>
        <div className={styles.shortlistRattingWrapper}>
          {showRatings ? (
            <div className={styles.ratingRow}>
              <div className={styles.ratingRowInner}>
                <ReviewSummaryRating
                  reviews_rating={reviews_rating}
                  reviews_count={reviews_count}
                  review_summary={review_summary}
                  propertyId={id}
                  className={formatClassNames(styles.trustPilotPill, styles.desktop)}
                  testId="searchPage-inventoryCard-trustpilotPill"
                />
                {/* Hide shortlist button for whitelabel in iframe */}
                {!(isWhitelabel && isIframe) && (
                  <ShortListButton
                    inventoryId={id}
                    noAbosolute
                    dataTestId="searchPage-inventoryCard-shortlistButton"
                    inventory={props}
                    shortlistedCardID={shortlistedCardID}
                    wrapperClassName={styles.shortlistButton}
                  />
                )}
              </div>
              <PropertyTags
                inventory={props}
                showCashbackValue
                className={styles.propertyCardTag}
              />
            </div>
          ) : (
            <>
              <PropertyTags
                inventory={props}
                showCashbackValue
                className={styles.propertyCardTag}
              />
              {/* Hide shortlist button for whitelabel in iframe */}
              {!(isWhitelabel && isIframe) && (
                <ShortListButton
                  inventoryId={id}
                  noAbosolute
                  dataTestId="searchPage-inventoryCard-shortlistButton"
                  inventory={props}
                  shortlistedCardID={shortlistedCardID}
                  wrapperClassName={styles.shortlistButton}
                />
              )}
            </>
          )}
        </div>
        <BottomSection
          meta={meta_short}
          pricing={pricing}
          item={props}
          wrapRef={bottomRef}
          page={page}
          isShortlistPage={isShortlistPageCard}
          name={name}
          canonical_name={canonical_name}
          location={location}
          propertyLink={link}
          showViewProperty={showViewProperty}
          showEnquireButton={showEnquireButton}
          wrapperClassName={styles.bottomSection}
          className={styles.ctaClassName}
        />
      </div>
    </div>
  );

  return (
    <CustomLink
      className={formatClassNames(styles.desktopContainer, highlighted ? styles.highlighted : "")}
      role="button"
      tabIndex={0}
      onClick={handlePropertyCardClick}
      onMouseEnter={highlightInventory}
      onMouseLeave={highlightRemoveInventory}
      linkRef={ref}
      id={id}
      dataTestId="searchpage-inventoryCard"
      onKeyDown={onKeyDown}
      to={link}
      redirect
      target="_blank"
      style={
        isSaleOfferAvailableOnInventory
          ? { border: `1.5px solid ${borderColor}`, ...containerStyle }
          : containerStyle
      }
    >
      {isSaleOfferAvailableOnInventory ? (
        <>
          {isAgentView && isAgentViewOpened ? (
            <div style={{ width: "100%" }}>
              <SaleOfferBrandTag />
              <div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,.1),0 2px 4px -2px rgba(0,0,0,.05)",
                  }}
                >
                  <ImageCarouselWithTags
                    images={images}
                    index={index}
                    videos={videos}
                    // reviewsRating={reviews_rating}
                    // reviewsCount={reviews_count}
                    // showRatings={showRatings}
                    wrapRef={imageWrapperRef}
                    inventoryData={props}
                    isDesktop
                    isAgentView={isAgentView}
                  />
                  {renderContent()}
                </div>
                <AgentViewCard inventory={props} />
              </div>
            </div>
          ) : (
            <div style={{ width: "100%" }}>
              <SaleOfferBrandTag />
              <div style={{ width: "100%", display: "flex" }}>
                <ImageCarouselWithTags
                  images={images}
                  index={index}
                  videos={videos}
                  // reviewsRating={reviews_rating}
                  // reviewsCount={reviews_count}
                  // showRatings={showRatings}
                  wrapRef={imageWrapperRef}
                  inventoryData={props}
                  isAgentView={isAgentView}
                  isDesktop
                />
                {renderContent()}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {isAgentView && isAgentViewOpened ? (
            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,.1),0 2px 4px -2px rgba(0,0,0,.05)",
                }}
              >
                <ImageCarouselWithTags
                  images={images}
                  index={index}
                  videos={videos}
                  // reviewsRating={reviews_rating}
                  // reviewsCount={reviews_count}
                  // showRatings={showRatings}
                  wrapRef={imageWrapperRef}
                  inventoryData={props}
                  isDesktop
                  isAgentView={isAgentView}
                />
                {renderContent()}
              </div>
              <AgentViewCard inventory={props} />
            </div>
          ) : (
            <>
              <ImageCarouselWithTags
                images={images}
                index={index}
                videos={videos}
                // reviewsRating={reviews_rating}
                // reviewsCount={reviews_count}
                // showRatings={showRatings}
                wrapRef={imageWrapperRef}
                inventoryData={props}
                isAgentView={isAgentView}
                isDesktop
              />
              {renderContent()}
            </>
          )}
        </>
      )}
    </CustomLink>
  );
};

export default wrapperHOC(memo(SearchListItemDesktop), {
  componentName: "SearchListItemDesktop-SearchPage",
  showForChina: true,
});
